import React, { useContext } from "react";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import { AppContext } from "../../context/app.context";
import { ScheduleContext } from "../../context/schedule.context";
import { isComplianceHeld } from "../../utility/complianceHold";
import { ModelCxContext } from "../../context/modelCx.context";
import { MILLISECONDS_PER_DAY } from "../../utility/keys";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// #154 — Repair Order pre-assigned-installer confirmation modal.
//
// Repair Orders arrive in Scheduler with the `installer` field already
// populated (by FileMaker's auto-invoice-create flow — the original
// installer who did the work the repair is for). Before #154, dragging
// such a Repair Order silently overwrote that payroll# (cell drop → drop
// target's installer; unassigned/RTS drop → empty). The user never saw
// the original payroll# being wiped.
//
// This modal asks the user what they want to do with the pre-filled
// installer. The choices vary by drop type:
//
//   "cell" (different installer):
//     - Keep original payroll# (job lands on ORIGINAL installer's row at
//       the dropped date — not the drop target's row)
//     - Reassign to the drop target's installer (existing behavior)
//     - Cancel
//
//   "unassigned-with-date" (Unassigned_Mon..Sun row):
//     - Keep original payroll# + schedule on original installer's row at
//       the dropped date
//     - Clear payroll# + keep the Repair Order SCHEDULED on the dropped
//       date with no installer — it appears in the Unassigned row at
//       that date, same as any other Unassigned-row drop (Brandon's
//       2026-05-22 clarification — see issue #154 thread).
//     - Cancel
//
//   "rts" (Unassigned_RTS):
//     - Keep original payroll# + unscheduled (status rolls back to 1/2/3
//       per FM flags — same as a normal Job unschedule; Brandon's
//       2026-05-29 #166 follow-up — was status 30 pre-fix)
//     - Clear original payroll# + unscheduled (same rollback status)
//     - Cancel
//
// Same-installer cell drops are filtered out before this modal opens
// (installer is not changing → no decision needed). Suppressed-row drops
// continue through the existing SuppressModal flow (separate concern).
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/**
 * Pure: parses a drop ID and returns the drop's date string ("" when the
 * drop has no associated date — RTS case). Mirrors the logic in
 * `computeJobInitValues` so this modal's payload preview matches what
 * processDragNDrop ultimately commits.
 */
export function getDropDateForRepairOrder({ dropObject, weekDatesArray }) {
  if (!dropObject || !dropObject.id) return "";
  if (dropObject.id === "Unassigned_RTS") return "";
  const parts = dropObject.id.split("_");
  if (dropObject.id.slice(0, 10) === "Unassigned") {
    const dayMap = { Mon: 0, Tue: 1, Wed: 2, Thr: 3, Fri: 4, Sat: 5, Sun: 6 };
    const dayIdx = dayMap[parts[1]];
    if (dayIdx === undefined) return "";
    const encodedDate = parts.length >= 3 ? parts.slice(2).join("_") : null;
    return encodedDate || weekDatesArray[dayIdx] || "";
  }
  // Crew cell drop: [payrollNo, columnIndex, date?]
  const colIdx = parseInt(dropObject.id.slice(8), 10);
  const encodedDate = parts.length >= 3 ? parts.slice(2).join("_") : null;
  return encodedDate || (weekDatesArray ? weekDatesArray[colIdx] : "") || "";
}

/**
 * Pure: computes the new estimatedCompletion date preserving the original
 * job's day-difference. Matches `computeJobInitValues`'s behavior so the
 * payload preview stays accurate.
 */
export function computeEstimatedCompletion({ foundDocument, dropDate }) {
  if (!dropDate || dropDate === "") return "";
  let dayDifference = 0;
  const orig = foundDocument?.date_scheduled;
  if (orig && orig !== "") {
    const origEst =
      foundDocument.date_estimatedCompletion && foundDocument.date_estimatedCompletion !== ""
        ? foundDocument.date_estimatedCompletion
        : orig;
    const ms = new Date(origEst).getTime() - new Date(orig).getTime();
    dayDifference = Math.round(ms / (1000 * 3600 * 24));
  }
  return new Date(
    Date.parse(dropDate) + MILLISECONDS_PER_DAY * dayDifference
  ).toLocaleDateString("en-us");
}

/**
 * Pure: builds the `pendingDragUpdate` payload for each of the six possible
 * user choices. Extracted so tests verify the payload byte-for-byte without
 * spinning up the React tree.
 *
 * @param {Object} params
 * @param {string} params.choice - One of:
 *   "cell-keep"          → cell drop, keep original installer
 *   "cell-reassign"      → cell drop, reassign to drop target
 *   "unassigned-keep"    → unassigned-with-date drop, keep + schedule on
 *                          original installer
 *   "unassigned-clear"   → unassigned-with-date drop, revert to Open
 *                          Repair Order state
 *   "rts-keep"           → RTS drop, keep payroll# + unscheduled
 *   "rts-clear"          → RTS drop, clear payroll# + unscheduled
 * @param {Object} params.foundDocument - The Repair Order being dragged.
 *   Read-only (must not be mutated per #85).
 * @param {string} params.dropTargetInstaller - Payroll# of the cell the
 *   user dropped on (only used by "cell-reassign"). "" for unassigned/RTS.
 * @param {string} params.dropDate - Drop-target date string ("" for RTS).
 * @returns {Object} pendingDragUpdate payload.
 */
export function buildRepairOrderPendingUpdate({
  choice,
  foundDocument,
  dropTargetInstaller,
  dropDate,
}) {
  const originalInstaller = foundDocument?.installer ?? "";
  const estComp = computeEstimatedCompletion({ foundDocument, dropDate });
  // #154 follow-up (Brandon, 2026-05-29 via #166 comment): rts-keep /
  // rts-clear now use the same rollback-status logic as the existing
  // RTS-revert path in schedule.context.js + the #166 WO Workorder modal.
  // Was: hardcoded statusCode 30 ("Open Repair Order") for both RTS
  // paths, which put the record in the Created/Verified list rather
  // than RTS. Brandon's invoice 2526577 example (RO at scheduled →
  // dragged back to RTS) was landing in C/V instead of RTS. New behavior:
  // status rolls back to 1/2/3 based on FM flags, same as a normal Job
  // unschedule, so the RO lands in the RTS list as the user expects.
  const rtsRollbackStatus = foundDocument?.notVerifiedByStaff
    ? 1
    : foundDocument?.notMaterialReceived
    ? 2
    : 3;

  switch (choice) {
    case "cell-keep":
      return {
        date_scheduled: dropDate,
        date_estimatedCompletion: estComp,
        installer: originalInstaller,
        statusCode: 4,
      };
    case "cell-reassign":
      return {
        date_scheduled: dropDate,
        date_estimatedCompletion: estComp,
        installer: dropTargetInstaller,
        statusCode: 4,
      };
    case "unassigned-keep":
      // Schedule on original installer's row at the dropped date.
      return {
        date_scheduled: dropDate,
        date_estimatedCompletion: estComp,
        installer: originalInstaller,
        statusCode: 4,
        extraCellId: null,
        extraCellText: null,
      };
    case "unassigned-clear":
      // Stay SCHEDULED on the dropped date but with empty installer.
      // The Repair Order appears in the Unassigned row at the dropped
      // date — same render as a non-Repair-Order job dragged onto an
      // Unassigned-row slot. Brandon's bug report (2026-05-22): clicking
      // this used to revert the record to the no-date Open-Repair-Order
      // state (status 30, date ""), which sent it back to the
      // Created/Verified panel and discarded the chosen schedule date.
      // The new behavior preserves the date and only clears the
      // installer payroll#, matching Brandon's intent and the existing
      // JobInitModal flow for any other classification dropped onto an
      // Unassigned-row slot.
      return {
        date_scheduled: dropDate,
        date_estimatedCompletion: estComp,
        installer: "",
        statusCode: 4,
        extraCellId: null,
        extraCellText: null,
      };
    case "rts-keep":
      // Unscheduled but payroll# preserved. statusCode rolls back per
      // FM flags (1/2/3) — see the rtsRollbackStatus comment above for
      // the rationale (Brandon's 2026-05-29 #166-comment feedback).
      return {
        date_scheduled: "",
        date_estimatedCompletion: "",
        installer: originalInstaller,
        statusCode: rtsRollbackStatus,
        extraCellId: null,
        extraCellText: null,
      };
    case "rts-clear":
      return {
        date_scheduled: "",
        date_estimatedCompletion: "",
        installer: "",
        statusCode: rtsRollbackStatus,
        extraCellId: null,
        extraCellText: null,
      };
    default:
      // Defensive: never happens in production. Returning {} signals "no
      // change" to buildDragNDropUpdateFields (all fields would compare
      // equal to foundDocument).
      return {};
  }
}

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

function RepairOrderConfirmModal({ setOpenRepairOrderConfirmModal }) {
  const {
    openRepairOrderConfirmModal,
    weekDatesArray,
    dataFormatting_payrollFields,
    setOpenJobInitModal,
  } = useContext(AppContext);
  const { awsgwCandidatesResult } = useContext(ModelCxContext);
  const {
    setDragObject,
    setDropObject,
    setProcessingDragNDrop,
    setPendingDragUpdate,
    dropObject,
  } = useContext(ScheduleContext);

  const foundDocument = openRepairOrderConfirmModal?.foundDocument || {};
  const dropType = openRepairOrderConfirmModal?.dropType || "cell";
  const dropTargetInstaller = openRepairOrderConfirmModal?.dropTargetInstaller || "";
  const dropDate = getDropDateForRepairOrder({ dropObject, weekDatesArray });
  const originalInstaller = foundDocument.installer || "";

  // - - - - - - - - - -

  // Brandon's 2026-05-22 UAT feedback: cell + unassigned-with-date drops
  // must STILL show the regular `JobInitModal` after the #154 question so
  // the user picks arrival time + adjusts est complete + saves through
  // the existing reschedule-reason flow. The #154 modal only ADDS the
  // installer-payroll# question to the front of the existing flow — it
  // doesn't replace it.
  //
  // RTS drops bypass JobInitModal because there's no arrival window /
  // est complete to pick for an unscheduled record. Those still dispatch
  // directly through `processDragNDrop`.
  const dispatchChoice = (choice) => {
    if (choice === "rts-keep" || choice === "rts-clear") {
      const payload = buildRepairOrderPendingUpdate({
        choice,
        foundDocument,
        dropTargetInstaller,
        dropDate,
      });
      setPendingDragUpdate(payload);
      setProcessingDragNDrop(true);
      setOpenRepairOrderConfirmModal({ show: false });
      return;
    }
    // Cell + Unassigned-with-date: chain to JobInitModal with the
    // user's installer choice pre-selected. JobInitModal's existing
    // `computeJobInitValues` already produces the right defaults for:
    //   - cell-reassign  → installer = dropTargetInstaller (default cell behavior)
    //   - unassigned-clear → installer = ""                (default unassigned behavior)
    // For the two "Keep" choices we pass an override:
    //   - cell-keep        → installer = originalInstaller
    //   - unassigned-keep  → installer = originalInstaller (rendered on
    //                        original installer's row at the dropped date)
    const repairOrderInstallerOverride =
      choice === "cell-keep" || choice === "unassigned-keep"
        ? originalInstaller
        : null;
    setOpenRepairOrderConfirmModal({ show: false });
    setOpenJobInitModal({
      show: true,
      repairOrderInstallerOverride,
    });
  };

  const onCancel = () => {
    setDragObject("");
    setDropObject({ id: "" });
    setOpenRepairOrderConfirmModal({ show: false });
  };

  // - - - - - - - - - -

  // Compliance hold check (mirrors JobInitModal's useEffect; only relevant
  // when the user is REASSIGNING to a new installer on a specific date).
  const isComplianceHoldBlockingReassign = (() => {
    if (dropType !== "cell") return false;
    if (!dropTargetInstaller || !dropDate) return false;
    // #352 — via the shared predicate, so this reassign-block agrees with the
    // icon and the assign hard-block (Mervin's ask: one validation rule).
    const installerDoc = awsgwCandidatesResult?.find(
      (doc) => dataFormatting_payrollFields(doc.payrollNo) === dropTargetInstaller
    );
    return isComplianceHeld(installerDoc, dropDate);
  })();

  // - - - - - - - - - -

  const renderHeading = () => {
    if (dropType === "cell") {
      return (
        <>
          This Repair Order is pre-assigned to installer{" "}
          <span className="font-bold">{originalInstaller}</span>. You dropped it on{" "}
          <span className="font-bold">{dropTargetInstaller}</span>
          {dropDate ? (
            <>
              {" "}
              for <span className="font-bold">{dropDate}</span>
            </>
          ) : null}
          . Which installer should keep the assignment?
        </>
      );
    }
    if (dropType === "unassigned-with-date") {
      return (
        <>
          This Repair Order is pre-assigned to installer{" "}
          <span className="font-bold">{originalInstaller}</span>. You dropped it on{" "}
          <span className="font-bold">Unassigned</span>
          {dropDate ? (
            <>
              {" "}
              for <span className="font-bold">{dropDate}</span>
            </>
          ) : null}
          . Keep the payroll# or clear it?
        </>
      );
    }
    // rts
    return (
      <>
        This Repair Order is pre-assigned to installer{" "}
        <span className="font-bold">{originalInstaller}</span>. You dropped it on{" "}
        <span className="font-bold">RTS</span> (unscheduled). Keep the payroll# on
        the record or clear it?
      </>
    );
  };

  const renderButtons = () => {
    if (dropType === "cell") {
      return (
        <>
          <Button
            className="uppercase"
            onClick={() => dispatchChoice("cell-keep")}
          >
            Keep {originalInstaller}
          </Button>
          <Button
            className="uppercase"
            disabled={isComplianceHoldBlockingReassign}
            title={
              isComplianceHoldBlockingReassign
                ? "Drop target is on compliance hold for this date."
                : undefined
            }
            onClick={() =>
              !isComplianceHoldBlockingReassign && dispatchChoice("cell-reassign")
            }
          >
            Reassign to {dropTargetInstaller}
          </Button>
          <Button color="light" className="uppercase" onClick={onCancel}>
            Cancel
          </Button>
        </>
      );
    }
    if (dropType === "unassigned-with-date") {
      return (
        <>
          <Button
            className="uppercase"
            onClick={() => dispatchChoice("unassigned-keep")}
          >
            Keep {originalInstaller} on {dropDate}
          </Button>
          <Button
            className="uppercase"
            onClick={() => dispatchChoice("unassigned-clear")}
          >
            Clear & leave Unassigned
          </Button>
          <Button color="light" className="uppercase" onClick={onCancel}>
            Cancel
          </Button>
        </>
      );
    }
    // rts
    return (
      <>
        <Button className="uppercase" onClick={() => dispatchChoice("rts-keep")}>
          Keep {originalInstaller} (unscheduled)
        </Button>
        <Button className="uppercase" onClick={() => dispatchChoice("rts-clear")}>
          Clear payroll#
        </Button>
        <Button color="light" className="uppercase" onClick={onCancel}>
          Cancel
        </Button>
      </>
    );
  };

  return (
    <Modal onClose={onCancel} disableCloseOnClickOutSide>
      <Modal.Header>
        Repair Order#{" "}
        <span className="font-bold">{foundDocument.invoiceNo}</span>
      </Modal.Header>
      <Modal.Content>
        <div className="flex flex-col gap-3 max-w-lg">
          <div>{renderHeading()}</div>
          {foundDocument.customer_lastName ? (
            <div className="text-xs opacity-70">
              Customer: {foundDocument.customer_lastName},{" "}
              {foundDocument.customer_firstName}
            </div>
          ) : null}
          {isComplianceHoldBlockingReassign ? (
            <div className="text-xs font-bold" style={{ color: "#dc2626" }}>
              Reassignment blocked: target installer is on compliance hold for
              this date.
            </div>
          ) : null}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div className="h-8 flex flex-wrap gap-3 justify-center">
          {renderButtons()}
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default RepairOrderConfirmModal;
